import { Link } from 'react-router';
import {
  SEO,
  BreadcrumbSchema,
  ItemListSchema,
  CollectionPageSchema,
} from '@/components/seo/seo';

interface Guide {
  slug: string;
  category: string;
  categoryColor: 'brand' | 'accent' | 'success' | 'warning';
  title: string;
  description: string;
  readTime: string;
}

interface GuideCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  guides: Guide[];
}

const categories: GuideCategory[] = [
  {
    id: 'high-intent-claim-help',
    title: 'Choose and Manage Your Claim Route',
    description: 'Task-specific England and Wales guidance for the decisions drivers face next',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    ),
    guides: [
      {
        slug: 'national-highways-pothole-claims',
        category: 'Claim routes',
        categoryColor: 'brand',
        title: 'National Highways Pothole Claims',
        description: 'Use the official Red Claim route for English strategic roads',
        readTime: '7 min',
      },
      {
        slug: 'england-wales-pothole-claims',
        category: 'Legal',
        categoryColor: 'success',
        title: 'Pothole Claims in England vs Wales',
        description: 'Understand the shared law and different road-authority routes',
        readTime: '7 min',
      },
      {
        slug: 'request-road-inspection-records',
        category: 'Evidence',
        categoryColor: 'accent',
        title: 'Request Road Inspection Records',
        description: 'Focused EIR and FOI templates for inspection and repair records',
        readTime: '8 min',
      },
      {
        slug: 'council-claim-or-car-insurance',
        category: 'Decision guide',
        categoryColor: 'warning',
        title: 'Council Claim or Car Insurance?',
        description: 'Compare direct claims, policy excess and no-claims considerations',
        readTime: '7 min',
      },
      {
        slug: 'dashcam-pothole-evidence',
        category: 'Evidence',
        categoryColor: 'accent',
        title: 'Dashcam Evidence for Pothole Claims',
        description: 'Preserve original footage, metadata and safe photographs',
        readTime: '7 min',
      },
      {
        slug: 'pothole-claim-rejected-next-steps',
        category: 'Escalation',
        categoryColor: 'warning',
        title: 'Pothole Claim Rejected? Next Steps',
        description: 'Assess Section 58 records and decide when to seek legal help',
        readTime: '8 min',
      },
      {
        slug: 'highway-authority-claim-routes',
        category: 'Directory',
        categoryColor: 'brand',
        title: 'Highway Authority Claim Routes',
        description: 'Search selected official routes verified against primary sources',
        readTime: '6 min',
      },
    ],
  },
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'New to pothole claims? Start here to learn the basics',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    guides: [
      {
        slug: 'what-makes-a-valid-claim',
        category: 'Getting Started',
        categoryColor: 'brand',
        title: 'What Makes a Valid Pothole Claim?',
        description: 'Understand when you can claim and what councils are responsible for',
        readTime: '5 min',
      },
      {
        slug: 'who-is-responsible',
        category: 'Getting Started',
        categoryColor: 'brand',
        title: 'Who Is Responsible for the Road?',
        description: 'How to identify whether it\'s your council, National Highways, or another authority',
        readTime: '4 min',
      },
      {
        slug: 'claim-timeline',
        category: 'Getting Started',
        categoryColor: 'brand',
        title: 'How Long Does a Claim Take?',
        description: 'Realistic timelines from submission to payout',
        readTime: '3 min',
      },
      {
        slug: 'fix-my-street-pothole-reporting',
        category: 'Getting Started',
        categoryColor: 'brand',
        title: 'Fix My Street: Reporting Potholes in Your Area',
        description: 'How FixMyStreet works and why your report becomes legal evidence',
        readTime: '5 min',
      },
    ],
  },
  {
    id: 'evidence',
    title: 'Evidence & Documentation',
    description: 'How to gather and present the strongest possible evidence',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    guides: [
      {
        slug: 'photographing-pothole-damage',
        category: 'Evidence',
        categoryColor: 'accent',
        title: 'How to Photograph Pothole Damage',
        description: 'Take photos that clearly show the defect and support your claim',
        readTime: '6 min',
      },
      {
        slug: 'documenting-vehicle-damage',
        category: 'Evidence',
        categoryColor: 'accent',
        title: 'Documenting Your Vehicle Damage',
        description: 'What photos you need and how to link damage to the incident',
        readTime: '5 min',
      },
      {
        slug: 'getting-repair-quotes',
        category: 'Evidence',
        categoryColor: 'accent',
        title: 'Getting the Right Repair Quote',
        description: 'What garages should include and why itemisation matters',
        readTime: '4 min',
      },
      {
        slug: 'pothole-claim-letter-template',
        category: 'Evidence',
        categoryColor: 'accent',
        title: 'Pothole Claim Letter Template',
        description: 'A free, ready-to-use claim letter template with copy-paste text',
        readTime: '6 min',
      },
    ],
  },
  {
    id: 'legal',
    title: 'Know Your Rights',
    description: 'Understand the law that\'s on your side',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
      </svg>
    ),
    guides: [
      {
        slug: 'section-41-explained',
        category: 'Legal',
        categoryColor: 'success',
        title: 'Section 41 of the Highways Act Explained',
        description: 'The law that makes councils responsible for road maintenance',
        readTime: '6 min',
      },
      {
        slug: 'section-58-defence',
        category: 'Legal',
        categoryColor: 'success',
        title: 'The Section 58 Defence: What Councils Must Prove',
        description: 'Understanding the council\'s main defence and how to counter it',
        readTime: '7 min',
      },
      {
        slug: 'prior-reports-explained',
        category: 'Legal',
        categoryColor: 'success',
        title: 'Why Prior Reports Are So Important',
        description: 'How previous pothole reports can make or break a council\'s defence',
        readTime: '5 min',
      },
    ],
  },
  {
    id: 'escalation',
    title: 'If Your Claim Is Rejected',
    description: 'What to do when the council says no',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    guides: [
      {
        slug: 'understanding-rejection-reasons',
        category: 'Escalation',
        categoryColor: 'warning',
        title: 'Common Rejection Reasons (And How to Respond)',
        description: 'Why councils reject claims and what you can do about it',
        readTime: '6 min',
      },
      {
        slug: 'small-claims-court-guide',
        category: 'Escalation',
        categoryColor: 'warning',
        title: 'Small Claims Court: A Complete Guide',
        description: 'Everything you need to know about escalating to court',
        readTime: '10 min',
      },
      {
        slug: 'what-to-expect-at-hearing',
        category: 'Escalation',
        categoryColor: 'warning',
        title: 'What to Expect at Your Hearing',
        description: 'How small claims hearings work and how to prepare',
        readTime: '8 min',
      },
    ],
  },
];

const categoryColorClasses = {
  brand: 'bg-brand-500 text-white',
  accent: 'bg-accent-500 text-white',
  success: 'bg-success-500 text-white',
  warning: 'bg-warning-500 text-brand-950',
};

export function GuidesPage() {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Guides', url: '/guides' },
  ];

  const allGuides = categories.flatMap((category) =>
    category.guides.map((guide) => ({
      name: guide.title,
      url: `/guides/${guide.slug}`,
      description: guide.description,
    }))
  );

  return (
    <div className="relative">
      <SEO
        title="Pothole Claim Guides | Evidence, Process & Templates | Fixtyer"
        description="Everything you need to know about claiming compensation for pothole damage. Clear, practical guides covering evidence, legal rights, and the claims process."
        canonical="/guides"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <CollectionPageSchema
        name="Pothole Claim Guides"
        description="Practical guides covering evidence, legal rights, and the UK pothole damage claims process."
        url="/guides"
      />
      <ItemListSchema
        name="Pothole Claim Guides"
        description="All Fixtyer guides to claiming compensation for pothole damage in the UK."
        url="/guides"
        items={allGuides}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-20">
        {/* Top diagonal stripe bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-brand-600 mb-6">
            <Link to="/" className="hover:text-brand-700 transition-colors">Home</Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">Guides</span>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-brand-700 text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            {categories.reduce((acc, cat) => acc + cat.guides.length, 0)} helpful guides
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold text-brand-950 leading-tight mb-6">
            Pothole Claim{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-accent-600">
              Guides
            </span>
          </h1>
          <p className="text-lg text-surface-800 max-w-2xl mx-auto">
            Everything you need to know about claiming compensation for pothole damage. 
            Clear, practical advice to help you build the strongest possible claim.
          </p>
        </div>

        {/* Bottom diagonal stripe bar */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* Guides Content */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {categories.map((category) => (
              <div key={category.id}>
                {/* Category Header */}
                <div className="flex items-start gap-4 mb-8">
                  <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center text-brand-600 flex-shrink-0">
                    {category.icon}
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-bold text-brand-950">
                      {category.title}
                    </h2>
                    <p className="text-surface-800 mt-1">
                      {category.description}
                    </p>
                  </div>
                </div>

                {/* Guide Cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.guides.map((guide) => (
                    <Link
                      key={guide.slug}
                      to={`/guides/${guide.slug}`}
                      className="group block bg-white rounded-2xl border-2 border-surface-200 p-6 hover:border-brand-300 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${categoryColorClasses[guide.categoryColor]}`}>
                          {guide.category}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-surface-800">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {guide.readTime}
                        </span>
                      </div>

                      <h3 className="font-display text-lg font-semibold text-brand-950 mb-2 group-hover:text-brand-700 transition-colors">
                        {guide.title}
                      </h3>
                      <p className="text-surface-800 text-sm mb-4 line-clamp-2">
                        {guide.description}
                      </p>

                      <span className="inline-flex items-center gap-1 text-brand-600 font-medium text-sm group-hover:text-accent-600 transition-colors">
                        Read guide
                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial context — how the guides fit together */}
      <section className="bg-surface-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-brand-950 mb-6">
            How these guides fit together
          </h2>
          <div className="space-y-4 text-surface-700 leading-relaxed">
            <p>
              Every successful pothole claim in England and Wales rests on the same legal foundation.{' '}
              <Link to="/guides/section-41-explained" className="text-brand-600 font-medium hover:text-brand-700 underline decoration-brand-300 underline-offset-2">
                Section 41 of the Highways Act 1980
              </Link>{' '}
              places a duty on highway authorities to maintain their roads — and when they fail, you can claim 
              for the damage. The council's main escape route is the{' '}
              <Link to="/guides/section-58-defence" className="text-brand-600 font-medium hover:text-brand-700 underline decoration-brand-300 underline-offset-2">
                Section 58 defence
              </Link>
              : proving they had a "reasonable" system of inspection and repair. Beating the Section 58 defence 
              is what most of these guides are really about, whether that's through{' '}
              <Link to="/guides/prior-reports-explained" className="text-brand-600 font-medium hover:text-brand-700 underline decoration-brand-300 underline-offset-2">
                prior reports of the same pothole
              </Link>{' '}
              or the council's own DfT maintenance rating.
            </p>
            <p>
              Evidence decides claims. Councils rarely pay out on a description alone — they want{' '}
              <Link to="/guides/photographing-pothole-damage" className="text-brand-600 font-medium hover:text-brand-700 underline decoration-brand-300 underline-offset-2">
                photographs that prove the defect's size and location
              </Link>
              ,{' '}
              <Link to="/guides/documenting-vehicle-damage" className="text-brand-600 font-medium hover:text-brand-700 underline decoration-brand-300 underline-offset-2">
                damage documentation that links the harm to the incident
              </Link>
              , and{' '}
              <Link to="/guides/getting-repair-quotes" className="text-brand-600 font-medium hover:text-brand-700 underline decoration-brand-300 underline-offset-2">
                independent, itemised vehicle repair quotes
              </Link>{' '}
              that stand up to scrutiny. The evidence guides walk through exactly what to capture and why each 
              piece matters.
            </p>
            <p>
              Finally, a first rejection is not the end of the road. Councils reject a large share of claims as 
              standard practice, often with boilerplate Section 58 letters. Knowing{' '}
              <Link to="/guides/understanding-rejection-reasons" className="text-brand-600 font-medium hover:text-brand-700 underline decoration-brand-300 underline-offset-2">
                what to do when a council rejects your pothole claim
              </Link>{' '}
              — and when it's worth escalating to the{' '}
              <Link to="/guides/small-claims-court-guide" className="text-brand-600 font-medium hover:text-brand-700 underline decoration-brand-300 underline-offset-2">
                small claims court
              </Link>{' '}
              — is often the difference between giving up and getting paid.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-4">
            Ready to start your claim?
          </h2>
          <p className="text-brand-200 text-lg mb-8 max-w-xl mx-auto">
            Now you know what's involved, let's check if you have a valid claim. It's free and takes just 2 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-colors"
            >
              Start Your Free Check
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/how-it-works"
              className="inline-flex items-center justify-center px-8 py-4 bg-brand-800 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
            >
              See How It Works
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom stripe */}
      <div 
        className="h-8"
        style={{
          background: `repeating-linear-gradient(
            -45deg,
            #1e40af,
            #1e40af 40px,
            #f97316 40px,
            #f97316 80px
          )`
        }}
      />
    </div>
  );
}
